import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const UpdatePriceModal = ({ isOpen, onClose, unit, onUpdate }) => {
  const [pricingModel, setPricingModel] = useState(unit?.pricingModel || 'per_sqft');
  const [pricePerSeat, setPricePerSeat] = useState(unit?.pricePerSeat || 0);
  const [pricePerSqft, setPricePerSqft] = useState(unit?.pricePerSqft || 0);
  const [fixedMonthlyPrice, setFixedMonthlyPrice] = useState(unit?.fixedMonthlyPrice || 0);
  const [effectiveDate, setEffectiveDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  // Reset state when unit changes
  useEffect(() => {
    if (unit) {
      setPricingModel(unit?.pricingModel || 'per_sqft');
      setPricePerSeat(unit?.pricePerSeat || 0);
      setPricePerSqft(unit?.pricePerSqft || 0);
      setFixedMonthlyPrice(unit?.fixedMonthlyPrice || 0);
      setEffectiveDate(new Date()?.toISOString()?.split('T')?.[0]);
      setNotes('');
      setError('');
    }
  }, [unit?.id]);

  if (!isOpen || !unit) return null;

  // Calculate current and new revenue
  const calculateRevenue = (model, perSeat, perSqft, fixed) => {
    switch (model) {
      case 'per_seat':
        return (unit?.capacitySeats || 0) * perSeat;
      case 'per_sqft':
        return (unit?.capacitySqft || unit?.areaSqft || 0) * perSqft;
      case 'fixed':
        return fixed;
      default:
        return 0;
    }
  };

  const currentRevenue = calculateRevenue(
    unit?.pricingModel,
    unit?.pricePerSeat || 0,
    unit?.pricePerSqft || 0,
    unit?.fixedMonthlyPrice || 0
  );

  const newRevenue = calculateRevenue(pricingModel, pricePerSeat, pricePerSqft, fixedMonthlyPrice);
  const revenueChange = newRevenue - currentRevenue;
  const revenueChangePercent = currentRevenue > 0 ? ((revenueChange / currentRevenue) * 100)?.toFixed(1) : 0;

  // Determine which pricing fields to show based on unit type
  const showPricingModelSelector = unit?.unitType === 'managed_office';
  const defaultPricingModel = unit?.unitType === 'coworking' ? 'per_seat' : 
                              unit?.unitType === 'bareshell' ? 'per_sqft' : 
                              pricingModel;

  const handleSubmit = () => {
    let validationError = '';

    if (pricingModel === 'per_seat' && pricePerSeat < 100) {
      validationError = 'Price per seat must be at least ₹100';
    } else if (pricingModel === 'per_sqft' && pricePerSqft < 10) {
      validationError = 'Price per sqft must be at least ₹10';
    } else if (pricingModel === 'fixed' && fixedMonthlyPrice < 1000) {
      validationError = 'Fixed monthly price must be at least ₹1,000';
    }

    if (validationError) {
      setError(validationError);
      return;
    }

    onUpdate({
      unitId: unit?.id,
      oldPricingModel: unit?.pricingModel,
      pricingModel: showPricingModelSelector ? pricingModel : defaultPricingModel,
      pricePerSeat: pricingModel === 'per_seat' ? pricePerSeat : null,
      pricePerSqft: pricingModel === 'per_sqft' ? pricePerSqft : null,
      fixedMonthlyPrice: pricingModel === 'fixed' ? fixedMonthlyPrice : null,
      effectiveDate,
      notes
    });
  };

  const getUnitTypeLabel = (type) => {
    const labels = {
      coworking: 'Coworking',
      bareshell: 'Bare Shell',
      managed_office: 'Managed Office',
      furnished: 'Furnished'
    };
    return labels?.[type] || type;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background bg-opacity-80">
      <div className="bg-card rounded-lg shadow-brand-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="border-b border-border p-4 md:p-6 flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              Update Pricing
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Unit {unit?.unitNumber} • Floor {unit?.floor} • {getUnitTypeLabel(unit?.unitType)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          {/* Current Pricing */}
          <div className="bg-muted bg-opacity-30 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Current Monthly Revenue</span>
              <span className="text-lg font-semibold text-foreground">
                ₹{currentRevenue?.toLocaleString('en-IN')}
              </span>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Model: {unit?.pricingModel?.replace('_', ' ')}</span>
              {unit?.pricingModel === 'per_seat' && (
                <span>₹{unit?.pricePerSeat?.toLocaleString('en-IN')}/seat × {unit?.capacitySeats} seats</span>
              )}
              {unit?.pricingModel === 'per_sqft' && (
                <span>₹{unit?.pricePerSqft?.toLocaleString('en-IN')}/sqft × {unit?.capacitySqft || unit?.areaSqft} sqft</span>
              )}
            </div>
          </div>

          {/* Pricing Model Selector (for managed_office only) */}
          {showPricingModelSelector && (
            <Select
              label="Pricing Model"
              value={pricingModel}
              onChange={(e) => {
                setPricingModel(e?.target?.value);
                setError('');
              }}
              options={[
                { value: 'per_seat', label: 'Per Seat' },
                { value: 'per_sqft', label: 'Per Sqft' },
                { value: 'fixed', label: 'Fixed Monthly' }
              ]}
              description="Choose how to price this managed office unit"
            />
          )}

          {/* Pricing Fields based on model */}
          {(pricingModel === 'per_seat' || (!showPricingModelSelector && unit?.unitType === 'coworking')) && (
            <div className="space-y-4">
              <Input
                label="Price per Seat (Monthly)"
                type="number"
                min="100"
                step="100"
                value={pricePerSeat}
                onChange={(e) => {
                  setPricePerSeat(parseInt(e?.target?.value) || 0);
                  setError('');
                }}
                error={error}
                description={`Unit has ${unit?.capacitySeats} seats`}
                required
              />
              <div className="bg-primary bg-opacity-5 rounded-lg p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Monthly Revenue:</span>
                  <span className="font-semibold text-foreground">
                    ₹{((unit?.capacitySeats || 0) * pricePerSeat)?.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {(pricingModel === 'per_sqft' || (!showPricingModelSelector && unit?.unitType === 'bareshell')) && (
            <div className="space-y-4">
              <Input
                label="Price per Sqft (Monthly)"
                type="number"
                min="10"
                step="10"
                value={pricePerSqft}
                onChange={(e) => {
                  setPricePerSqft(parseInt(e?.target?.value) || 0);
                  setError('');
                }}
                error={error}
                description={`Unit has ${unit?.capacitySqft || unit?.areaSqft} sqft`}
                required
              />
              <div className="bg-primary bg-opacity-5 rounded-lg p-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Monthly Revenue:</span>
                  <span className="font-semibold text-foreground">
                    ₹{((unit?.capacitySqft || unit?.areaSqft || 0) * pricePerSqft)?.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>
          )}

          {pricingModel === 'fixed' && (
            <Input
              label="Fixed Monthly Price"
              type="number"
              min="1000"
              step="1000"
              value={fixedMonthlyPrice}
              onChange={(e) => {
                setFixedMonthlyPrice(parseInt(e?.target?.value) || 0);
                setError('');
              }}
              error={error}
              description="Fixed price regardless of seats or sqft"
              required
            />
          )}

          {/* Revenue Change Summary */}
          {newRevenue > 0 && newRevenue !== currentRevenue && (
            <div className="bg-primary bg-opacity-5 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">New Monthly Revenue</span>
                <span className="text-base font-semibold text-foreground">
                  ₹{newRevenue?.toLocaleString('en-IN')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Revenue Change</span>
                <span className={`text-base font-semibold ${revenueChange >= 0 ? 'text-success' : 'text-error'}`}>
                  {revenueChange >= 0 ? '+' : ''}₹{revenueChange?.toLocaleString('en-IN')} ({revenueChangePercent}%)
                </span>
              </div>
            </div>
          )}

          <Input
            label="Effective Date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e?.target?.value)}
            description="When should this price change take effect?"
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
              placeholder="Add any notes about this price change..."
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>
        </div>

        <div className="border-t border-border p-4 md:p-6 flex gap-3 sticky bottom-0 bg-card">
          <Button
            variant="outline"
            onClick={onClose}
            fullWidth
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            iconName="Check"
            fullWidth
          >
            Update Pricing
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UpdatePriceModal;